import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Users } from "lucide-react";

/**
 * Slider showing current ambassadors: name + photo + how many students they brought,
 * plus a call-to-action to become an ambassador. Auto-rotates every 4s.
 */
export default function AmbassadorsSection({ ambassadors = [] }) {
  const list = Array.isArray(ambassadors) ? ambassadors : [];
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (list.length < 2) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % list.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [list.length]);

  if (list.length === 0) {
    return (
      <section
        className="py-16 lg:py-24 bg-gradient-to-bl from-amber-50/60 via-white to-teal-50/40"
        data-testid="ambassadors-section-empty"
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-10 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-4 py-1.5 text-xs font-bold text-amber-800">
            <Sparkles className="h-3.5 w-3.5" /> برنامج السفراء
          </div>
          <h2 className="mt-4 text-3xl sm:text-4xl font-heading font-black text-slate-900">
            كن أول سفراء عماد
          </h2>
          <p className="mt-4 text-slate-600 max-w-2xl mx-auto">
            سفراؤنا يعرّفون بمنصّة عماد داخل جامعاتهم ومجتمعاتهم، ويكسبون
            نقاطًا وامتيازات مع كل طالب يلتحق بفضلهم.
          </p>
          <Link
            to="/register?role=student&referral=ambassador"
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-teal-700 px-5 py-3 font-bold text-white hover:bg-teal-600"
            data-testid="ambassadors-cta-empty"
          >
            كن سفيراً <Sparkles className="h-4 w-4" />
          </Link>
        </div>
      </section>
    );
  }

  const current = list[index] || list[0];

  return (
    <section
      className="py-20 lg:py-28 bg-gradient-to-bl from-amber-50/50 via-white to-teal-50/40"
      data-testid="ambassadors-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-4 py-1.5 text-xs font-bold text-amber-800">
              <Sparkles className="h-3.5 w-3.5" /> برنامج السفراء
            </div>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-heading font-black text-slate-900 leading-tight">
              سفراء عماد يصنعون الفرق
            </h2>
            <p className="mt-4 text-slate-700 leading-relaxed text-lg">
              مجموعة من الطلاب المتميزين الذين يعرّفون بمنصّة عماد داخل
              جامعاتهم ومجتمعاتهم. تعرّف على أبرز سفرائنا وعدد الطلاب الذين
              التحقوا بفضلهم.
            </p>
            <Link
              to="/register?role=student&referral=ambassador"
              className="mt-6 inline-flex items-center gap-2 rounded-xl bg-teal-700 px-5 py-3 font-bold text-white hover:bg-teal-600"
              data-testid="ambassadors-cta"
            >
              كن سفيراً <Sparkles className="h-4 w-4" />
            </Link>
          </div>

          <div className="lg:col-span-7">
            <div
              key={current.id || index}
              className="rounded-3xl border border-slate-200 bg-white shadow-xl shadow-teal-900/5 p-6 sm:p-8 animate-in fade-in duration-500"
              data-testid="ambassador-card"
            >
              <div className="flex items-center gap-4 sm:gap-6">
                {current.photo_url ? (
                  <img
                    src={current.photo_url}
                    alt={current.name}
                    className="h-20 w-20 sm:h-24 sm:w-24 rounded-full object-cover ring-4 ring-teal-100"
                    data-testid="ambassador-photo"
                  />
                ) : (
                  <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-full bg-teal-50 grid place-items-center text-2xl font-black text-teal-700 ring-4 ring-teal-100">
                    {(current.name || "?")[0]}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-xl sm:text-2xl font-heading font-black text-slate-900" data-testid="ambassador-name">
                    {current.name || "سفير عماد"}
                  </div>
                  {current.affiliation && (
                    <div className="mt-1 text-sm text-slate-500">{current.affiliation}</div>
                  )}
                  <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-teal-50 px-3 py-1 text-sm font-bold text-teal-800" data-testid="ambassador-count">
                    <Users className="h-3.5 w-3.5" />
                    استقطب {current.students_count || 0} طالب
                  </div>
                </div>
              </div>
              {current.quote && (
                <p className="mt-6 border-r-4 border-amber-300 bg-amber-50/70 px-4 py-3 text-slate-800 rounded-lg text-sm leading-7">
                  «{current.quote}»
                </p>
              )}
              {list.length > 1 && (
                <div className="mt-6 flex items-center justify-center gap-1.5">
                  {list.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setIndex(i)}
                      className={`h-2 rounded-full transition-all ${
                        i === index ? "w-8 bg-teal-700" : "w-2 bg-slate-300"
                      }`}
                      aria-label={`السفير ${i + 1}`}
                      data-testid={`ambassador-dot-${i}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
